<template>
  <div 
  class="relative flex items-center lh-1 custom-checkbox" 
  :class="`${classCustom ? classCustom : isAlignCenter ? 'mb-0': 'mb-16'}`"
  >
    <input
      type="checkbox"
      class="hand w-18 h-18 absolute left-0 o-0"
      :id="id"
      :name="name"
      :value="value"
      :checked="isChecked"
      v-model="getValue"
    />
    <label class="dib hand pl-26" :for="id">
      <span class="dib lh-1 fs-14 lh-17 gray-dark">{{ label }}</span>
    </label>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted } from 'vue'

export default defineComponent({
  name: 'CheckBox',
  props: {
    id: { type: String, default: '', require: true },
    name: { type: String, default: '', require: true },
    classCustom: { type: String, default: '' },
    value: { type: [String, Number], require: true },
    label: { type: String, default: '', require: true },
    modelValue: { type: [Array, Object, Boolean, Number], default: null },
    isChecked: { type: Boolean, default: false },
    isAlignCenter: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'trigger'],
  setup(props, { emit }) {
    const getValue = computed({
      get: () => props.modelValue,

      set: (value) => {
        emit('update:modelValue', value)
        emit('trigger', value)
      },
    })

    const initChecked = () => {
      if (props.isChecked) getValue.value.push(props.value)
    }

    onMounted(() => {
      initChecked()
    })

    return { getValue }
  },
})
</script>

<style lang="scss" scoped>
.custom-checkbox {
  label {
    &::after,
    &::before {
      position: absolute;
      left: 0;
      top: 0;
      display: block;
      width: 18px; // v2 §Checkbox — box 16→18px
      height: 18px;
      content: '';
      transition: border-color $dur-fast $ease-out, background-color $dur-fast $ease-out; // §11
      border-radius: $radius-sm; // §10 (4px, was 3px)
    }
    &::before {
      pointer-events: none;
      border: $bw-thin solid $border-hairline; // v2 §Checkbox — resting border 1→1.5px
    }
    &::after {
      background-position: center;
      background-repeat: no-repeat;
    }
  }
  input:checked ~ label {
    &::before {
      border-color: $brand-green; // keep the box ring in sync with the fill
    }
    &::after {
      background-color: $brand-green; // §9 checked fill (was brand,800)
      background-image: url('#{$assets-cdn}/icons/icon-mask.svg');
    }
  }
  input:hover ~ label,
  label:hover {
    &::before {
      border-color: $brand-green; // §9 hover — border toward green
      background-color: rgba($brand-green, 0.08);
    }
  }
  // §14 focus ring on the VISIBLE box (the native input is visually hidden).
  // v2 (L198): 2px GREEN ring, 2px offset. Hard-set green rather than
  // @include focus-ring — the mixin emits &:focus-visible on the element
  // itself, but here focus lives on the hidden input and the ring must render
  // on the sibling `label::before`, which the mixin can't express.
  input:focus-visible ~ label::before {
    outline: 2px solid $brand-green;
    outline-offset: 2px;
  }
  // §9 disabled — v2 checkbox-specific 40% (not the global 0.5) + not-allowed.
  input:disabled ~ label {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
</style>
